/**
 * WhyUsSection Component
 *
 * Diamond Architecture: Server Component for static "Why Choose Us" content.
 * Uses next-intl for server-side translations.
 *
 * Structure mirrors Rails WhyUsSectionComponent:
 * - 2-column grid (video left, content right)
 * - Value propositions with headings
 * - CTA link to services
 *
 * Video Strategy:
 * - Poster images served from public/ (LCP-critical, small files)
 * - Video served from CloudFront CDN (immutable static asset)
 * - LazyVideo component handles intersection observer loading
 */
import { useLocale, useTranslations } from '@/shared/i18n';
import { Link } from '@/shared/i18n/routing';
import { useConfig } from '@/shared/providers/ConfigProvider';
import { getChatDemoVideoUrl } from '@letsgethome/lib/cdn';
import { routes } from '@letsgethome/routes';
import type { Locale } from '@letsgethome/types/branded';
import { LazyVideo } from '@letsgethome/ui/LazyVideo';
import { ArrowRight } from 'lucide-react';

/**
 * Get the poster image path for the current locale.
 * Poster images are stored in public/home/why-us/ (LCP-critical).
 */
function getPosterPath(locale: Locale): string {
  // Normalize locale to match filename format (e.g., "zh-HK" → "zh_hk")
  const normalizedLocale = locale.toLowerCase().replace('-', '_');
  return `/home/why-us/chat_demo_${normalizedLocale}.webp`;
}

export function WhyUsSection() {
  const t = useTranslations('home.whyUs');
  const locale = useLocale();
  const config = useConfig();

  // Static-marketing asset → the marketing CDN (the staging distribution in
  // dev, where these global assets actually live; `config.cdnUrl` would resolve
  // to dev MinIO, which doesn't hold them — the bug this branding prevents).
  const videoUrl = getChatDemoVideoUrl(locale, config.marketingCdnUrl);
  const posterUrl = getPosterPath(locale);

  return (
    <section
      id="why-us"
      aria-labelledby="why-choose-us-heading"
      className="bg-background px-4 py-16 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid items-center gap-12 xl:grid-cols-2">
          {/* Left: Chat Demo Video */}
          <LazyVideo
            src={videoUrl}
            poster={posterUrl}
            aspect="aspect-square"
            objectFit="contain"
            rootMargin="240px 0px"
            threshold={0.01}
            alt={t('videoAlt')}
          />

          {/* Right: Content */}
          <div className="space-y-8">
            <h2
              id="why-choose-us-heading"
              className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl"
            >
              {t('heading')}
            </h2>

            {/* Value for Money */}
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-primary">{t('valueForMoney.heading')}</h3>
              <p className="text-lg text-muted-foreground">{t('valueForMoney.description')}</p>
            </div>

            {/* Serve Not Sell */}
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-primary">{t('serveNotSell.heading')}</h3>
              <p className="text-lg text-muted-foreground">{t('serveNotSell.description')}</p>
            </div>

            {/* CTA Link */}
            <Link
              href={routes.services()}
              prefetch="intent"
              className="inline-flex items-center gap-2 text-lg font-medium text-primary hover:underline"
            >
              {t('cta')}
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
